import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppliedFilters,
  Button,
  DataTable,
  Drawer,
  MainLayout,
  Tabs,
} from '../../../ui';
import AsyncCustomSelect from '../../../ui/custom_react_select/async_select';
import {
  ChevronRight,
  FilterIcon,
  SearchIconNewUI,
} from '../../../ui/icons/icons';
import { DatePicker } from '../../../ui/input/date_picker/date_picker';
import { TextInput } from '../../../ui/input/text_input/text_input';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import { RootState } from '../../../utils/redux_store/store';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { CreateJobOpeningDialog } from '../components';
import { getColumns } from './Functions';
import { useJobOpenings } from './Hooks';

export const JobOpeningsView = () => {
  const { t } = useTranslation(NS.adminPages);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const statuses: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.jobDescription), value: 'Job Description' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.jobPost), value: 'Job Post' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.shortlist), value: 'Shortlist' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.interview), value: 'Interview' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.decisionMaking), value: 'Decision Making' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.list.statusOptions.closed), value: 'Closed' },
  ];

  const {
    handleManagerChange,
    loadOptionsDebounced,
    defaultEmployeeUserOptions,
    jobTitle,
    setJobTitle,
    setAppliedJobTitle,
    jobReferenceNumber,
    setJobReferenceNumber,
    handleSortChange,
    jobOpeningData,
    onPageChange,
    pageNumber,
    totalPages,
    history,
    sort,
    appliedFilters,
    loading,
    handleClearFilters,
    handleRemoveFilter,
    handleStatus,
    hasFetchedOnce,
    totalRecords,
    setActiveTab,
    activeTab,
    tabs,
    tags,
    setTags,
    hiringManager,
    internalRecruiter,
    filterOpen,
    handleOpenFilter,
    handleCloseFilter,
    handleApplyFilters,
    isFiltersApplied,
    dateRange,
    setDateRange,
  } = useJobOpenings();

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const isFilterDisabled = (filterKey: string) => {
    return (
      isFiltersApplied &&
      appliedFilters[filterKey] &&
      appliedFilters[filterKey].length > 0
    );
  };

  const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  const hasAnyLicense = (
    isLicenseActive(subscriptions?.hierTalentBase) ||
        isLicenseActive(subscriptions?.hierTalentAutoClassify) ||
        isLicenseActive(subscriptions?.hierTalentJobPost) ||
        isLicenseActive(subscriptions?.hierTalentAutoAssessments) ||
        isLicenseActive(subscriptions?.hierTalentSmartInterviews) ||
        isLicenseActive(subscriptions?.hierTalentCca) ||
        isLicenseActive(subscriptions?.hierTalentCandiBank) ||
        isLicenseActive(subscriptions?.hierTalentWellness) ||
        isLicenseActive(subscriptions?.hierTalentPsychometricFaxtor)
  );

  return (
    <div className='w-full h-full flex flex-col gap-3 sm:gap-4 overflow-hidden'>
      <nav
          className='flex w-full flex-col gap-2 py-2 text-[13px] border-b border-slate-100 sm:flex-row sm:items-center sm:justify-between sm:py-3'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex min-w-0 items-center space-x-1 md:space-x-3'>
          <li className='inline-flex items-center'>
            <Link
                to={'#'}
                className='inline-flex items-center text-slate-500 hover:text-emerald-600 transition-colors duration-200'
            >
              <span className='font-medium'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.list.breadcrumbParent) }
              </span>
            </Link>
          </li>
          <li aria-current='page'>
            <div className='flex items-center'>
              <ChevronRight className='w-4 h-4 text-slate-400 mx-1' />
              <span className='ml-1 text-emerald-700 font-semibold'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.list.breadcrumbCurrent) }
              </span>
            </div>
          </li>
        </ol>

        <Link
            className='group flex items-center gap-1.5 self-start text-slate-600 font-semibold hover:text-emerald-700 transition-all duration-200 sm:self-auto'
            to={'/jobs'}
        >
          <span className='border-b border-transparent group-hover:border-emerald-700 pb-0.5'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.list.viewJobPosting) }
          </span>
          <ChevronRight className='w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5' />
        </Link>
      </nav>
      <div className='flex w-full flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
        <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setJobTitle('');
              onPageChange(1);
            }}
        />
        <div className='flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center xl:w-auto xl:flex-nowrap'>
          <div className='relative flex min-w-0 flex-1 items-center sm:min-w-[260px] xl:w-72 xl:flex-none'>
            <SearchIconNewUI className='absolute left-3 w-4 h-4 text-slate-700' />
            <input
                type='text'
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.searchPlaceholder, { tab: activeTab })}
                value={jobTitle}
                onChange={(e) => {
                  setAppliedJobTitle(e.target.value);
                }}
                className='min-h-10 w-full bg-slate-100/80 border border-slate-200/50 rounded-lg py-2 pl-10 pr-4 text-[13px] placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all font-medium text-slate-700'
            />
          </div>
          <Button
              label={t(ADMIN_PAGES_KEYS.jobOpenings.list.filter)}
              icon={<FilterIcon />}
              hasIcon
              onClick={handleOpenFilter}
              variant='secondary'
              className='w-full sm:w-auto'
          />
          <PermissionGate
              requiredPermissions={[
                Permission.ASSESSMENTS_CREATE,
                Permission.QUESTIONS_VIEW,
              ]}
              matchAnyPermission={false}
          >
            { hasAnyLicense ? (
              <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  onClick={() => setIsCreateModalOpen(true)}
                  className='min-h-10 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 hover:from-emerald-700 hover:to-emerald-800 focus:ring-emerald-500 relative inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed sm:w-auto cursor-pointer'
              >
                { t(ADMIN_PAGES_KEYS.jobOpenings.list.createNew) }
              </motion.button>
            ) : (
              <span
                  className='bg-slate-200 text-slate-400 cursor-not-allowed relative inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg'
                  title={t(ADMIN_PAGES_KEYS.jobOpenings.list.noActiveLicenses)}
              >
                { t(ADMIN_PAGES_KEYS.jobOpenings.list.createNew) }
              </span>
            ) }
          </PermissionGate>
        </div>
      </div>

      { /* Motion Create Job Opening Dialog */ }
      <CreateJobOpeningDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccessClose={() => setIsCreateModalOpen(false)}
      />
      <MainLayout className='flex-1 min-h-0'>
        { Object.keys(appliedFilters).length > 0 && (
          <AppliedFilters
              appliedFilters={appliedFilters}
              handleClearFilters={handleClearFilters}
              handleRemoveFilter={handleRemoveFilter}
          />
        ) }
        <div className='w-full h-[calc(100dvh-260px)] min-h-[360px] min-w-0 sm:h-[calc(100vh-230px)]'>
          <DataTable
              isLoading={!hasFetchedOnce || loading}
              columns={getColumns(t)}
              data={jobOpeningData}
              rowsPerPage={10}
              onPageChange={onPageChange}
              currentPage={pageNumber}
              totalPages={totalPages}
              onSortChange={handleSortChange}
              sortConfig={Object.entries(sort).map(([key, direction]) => ({
                key,
                direction,
              }))}
              onClickRow={(row) => {
                history.push(`/job-openings/${row['id']}`);
              }}
              totalRecords={totalRecords}
          />
        </div>
      </MainLayout>
      <Drawer
          isOpen={filterOpen}
          onClose={handleCloseFilter}
          footerContent={
            <div className='flex flex-col gap-3 bg-slate-50/50 p-4 border-t border-slate-100 rounded-b-2xl sm:flex-row sm:items-center sm:justify-between'>
              <Button
                  label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.cancel)}
                  variant='secondary'
                  className='!px-6 !py-2 !h-auto !text-[13px]'
                  onClick={handleCloseFilter}
              />
              <div className='flex flex-wrap gap-3 items-center'>
                <Button
                    label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.reset)}
                    variant='link'
                    className='!text-slate-400 hover:!text-slate-600 !px-4 !py-2 !h-auto !text-[13px] !font-semibold'
                    onClick={handleClearFilters}
                />
                <Button
                    label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.applyFilters)}
                    className='!px-8 !py-2 !h-auto !text-[13px] bg-emerald-600 hover:bg-emerald-700 !shadow-lg !shadow-emerald-500/20'
                    onClick={handleApplyFilters}
                />
              </div>
            </div>
          }
          title={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.title)}
          width='700px'
      >
        <div className='flex flex-col w-full p-4 sm:p-6 space-y-6'>
          <div className='space-y-3'>
            <label className='text-xs font-bold text-slate-500 uppercase tracking-wider'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.timeRange) }
            </label>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div className='space-y-1'>
                <p className='text-[11px] font-medium text-slate-400 ml-1'>
                  { t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.from) }
                </p>
                <DatePicker
                    asSingle={true}
                    useRange={false}
                    startDate={dateRange?.startDate ?? null}
                    onChange={(val) =>
                      setDateRange({
                        startDate: val?.startDate ?? null,
                        endDate: dateRange?.endDate ?? null,
                      })
                    }
                />
              </div>
              <div className='space-y-1'>
                <p className='text-[11px] font-medium text-slate-400 ml-1'>
                  { t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.to) }
                </p>
                <DatePicker
                    asSingle={true}
                    useRange={false}
                    startDate={dateRange?.endDate ?? null}
                    onChange={(val) =>
                      setDateRange({
                        startDate: dateRange?.startDate ?? null,
                        endDate: val?.startDate ?? null,
                      })
                    }
                />
              </div>
            </div>
          </div>
          <div className='grid grid-cols-1 gap-4 w-full sm:grid-cols-2'>
            <TextInput
                className='w-full'
                type='text'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.jobTitle)}
                name='jobTitle'
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={isFiltersApplied}
            />
            <TextInput
                className='w-full'
                type='text'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.jobReference)}
                name='jobReference'
                value={jobReferenceNumber}
                onChange={(e) => setJobReferenceNumber(e.target.value)}
                disabled={isFiltersApplied}
            />
            <AsyncCustomSelect
                className='w-full'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.hiringManager)}
                name='hiringManager'
                defaultOptions={defaultEmployeeUserOptions}
                onChange={(selectedOption: SelectOptions) =>
                  handleManagerChange(selectedOption, true)
                }
                loadOptions={(inputValue, callback) =>
                  loadOptionsDebounced(inputValue, callback)
                }
                isMulti={false}
                disabled={isFilterDisabled('Hiring Manager')}
                value={hiringManager}
            />
            <AsyncCustomSelect
                className='w-full'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.internalRecruiter)}
                name='internalRecruiter'
                defaultOptions={defaultEmployeeUserOptions}
                onChange={(selectedOption: SelectOptions) =>
                  handleManagerChange(selectedOption, false)
                }
                loadOptions={(inputValue, callback) =>
                  loadOptionsDebounced(inputValue, callback)
                }
                isMulti={false}
                disabled={isFilterDisabled('Internal Recruiter')}
                value={internalRecruiter}
            />
            <AsyncCustomSelect
                className='w-full'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.status)}
                name='status'
                defaultOptions={statuses}
                onChange={(selectedOption: SelectOptions) =>
                  handleStatus(selectedOption)
                }
                loadOptions={(inputValue, callback) => callback(statuses)}
                isMulti={false}
                disabled={isFiltersApplied}
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.statusPlaceholder)}
            />
            <TextInput
                className='w-full'
                type='text'
                label={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.tags)}
                name='tags'
                value={Array.isArray(tags) ? tags.join(', ') : tags}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const tagsArray = inputValue
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter((tag) => tag.length > 0);
                  setTags(tagsArray);
                }}
                disabled={isFiltersApplied}
                placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.list.drawer.tagsPlaceholder)}
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};
